import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    CheckCircleOutline,
    QuestionAnswer,
    Download,
    Home,
    Info,
    PeopleAlt,
    WbSunny,
    MeetingRoom,
    Wifi,
    Kitchen,
    LocalLaundryService,
    Security,
    AttachFile,
    LocationOn,
    Lock,
    EventNote,
    ThumbUpAlt,
    Close
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const checklistData = [
    { icon: <Home color="primary" />, label: '共用スペースの広さ・清潔さ', desc: 'リビングやキッチン、トイレ・バスなど共用部の使い勝手や清掃頻度を確認しましょう。' },
    { icon: <MeetingRoom color="primary" />, label: '個室・ドアの施錠', desc: 'プライバシー確保のため、個室や収納の鍵の有無も大切なポイント。' },
    { icon: <WbSunny color="primary" />, label: '日当たり・通風・騒音', desc: '窓の向きや外の音、部屋の明るさなど、住み心地を左右する要素を体感しましょう。' },
    { icon: <Wifi color="primary" />, label: 'ネット環境', desc: 'Wi-Fiの有無や速度、使用制限などを質問しましょう。' },
    { icon: <Kitchen color="primary" />, label: '設備の充実度', desc: '冷蔵庫、洗濯機、調理器具、掃除機などの備品の状態や数も要確認。' },
    { icon: <LocalLaundryService color="primary" />, label: '洗濯・乾燥スペース', desc: '洗濯機の利用ルールや干場のスペースも忘れずにチェック。' },
    { icon: <Security color="primary" />, label: 'セキュリティ', desc: 'オートロックや防犯カメラ、来客時の対応方法など安全面も重要です。' },
    { icon: <LocationOn color="primary" />, label: '周辺環境・アクセス', desc: '最寄り駅やスーパー、コンビニ、治安など、周辺の生活環境を歩いて確認。' },
    { icon: <AttachFile color="primary" />, label: '契約内容・費用', desc: '家賃や光熱費、初期費用、解約条件、保証人の有無など契約面も細かく質問。' },
];


const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>目次</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setShowToc(false)}
                                startIcon={<Close />}
                            >
                                閉じる
                            </Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {[
                                { no: 1, label: '導入', id: 'introduction' },
                                { no: 2, label: 'ルームシェア内見の基本ポイント', id: 'basic-points' },
                                { no: 3, label: '内見チェックリスト', id: 'checklist' },
                                { no: 4, label: '体験談＆実例アドバイス', id: 'experience' },
                                { no: 5, label: 'まとめ', id: 'summary' }
                            ].map(item => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#666',
                                        fontWeight: 'bold',
                                        borderRadius: 1,
                                        fontSize: '1.05rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {item.no}.
                                    </Box>
                                    <Link component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left' }}>
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                    startIcon={<Info />}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア内見で後悔しないために
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、新しい出会いやコストパフォーマンスの良さなど、魅力がたくさんあります。しかし、最初の「内見」でしっかりチェックしなければ、後々のトラブルや生活ストレスに繋がることもしばしば…。<br />
                    本記事では、ルームシェア経験者や物件案内を担当してきた立場から、「内見時に絶対チェックしたいポイント」と「具体的な質問例」、さらに使えるチェックリスト＆体験談まで、余すところなくご紹介します！<br />
                    気になる物件を見つけたら、このページを片手に、納得と安心のルームシェア生活をスタートしましょう。
                </Typography>
                <Box sx={{ my: 2, textAlign: 'center' }}>

                </Box>
            </Box>

            {/* 基本ポイント */}
            <Box id="basic-points" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェア内見の基本ポイント
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={2} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Home color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>住みやすさの確認</Typography>
                            </Box>
                            <Typography variant="body2">
                                <ul style={{ paddingLeft: 16, margin: 0 }}>
                                    <li>共用部の清潔さ・広さ・明るさ</li>
                                    <li>収納や個室のプライバシー</li>
                                    <li>家賃・光熱費の明確さ</li>
                                    <li>生活音や周辺環境（騒音・治安など）</li>
                                </ul>
                            </Typography>
                        </Paper>
                    </Grid>

                </Grid>
                <Box sx={{ my: 3, textAlign: 'center' }}>

                </Box>
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    まずは「住みやすいか」を軸に、物件を観察してみることが大切です。
                </Typography>
            </Box>


            {/* 内見チェックリスト */}
            <Box id="checklist" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    内見チェックリスト
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    「どこを見ておけばいいの？」と迷う方のために、内見時のチェックポイントをリストアップしました。
                </Typography>
                <List>
                    {checklistData.map((item, idx) => (
                        <ListItem key={idx} sx={{ alignItems: 'flex-start', pb: 1.2 }}>
                            <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                            <Box>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', display: 'inline' }}>
                                    {item.label}
                                </Typography>
                                <Typography variant="body2" sx={{ ml: 0.5, display: 'inline', color: 'text.secondary' }}>
                                    ：{item.desc}
                                </Typography>
                            </Box>
                        </ListItem>
                    ))}
                </List>
                <Box sx={{ mt: 3, mb: 1, textAlign: 'center' }}>

                </Box>
            </Box>


            {/* 体験談 */}
            <Box id="experience" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    体験談＆実例アドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', borderRadius: 2, bgcolor: '#fafafa', boxShadow: 1 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar src="/images/avatars/roommate1.jpg" alt="20代男性ルームシェア体験者の顔写真" sx={{ mr: 2 }} />
                                    <Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Kさん（30代・男性）</Typography>
                                        <Chip size="small" label="トラブル回避" color="primary" variant="outlined" sx={{ ml: 1 }} />
                                    </Box>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    内見時に「騒音は大丈夫ですか？」と聞いたら、夜間は静かで快適と説明されました。でも実際には、道路側の部屋は朝夕の車の音が結構響いて…。現地で窓を開けて確認する大切さを痛感しました！
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    <ThumbUpAlt fontSize="small" sx={{ mr: 0.5, color: '#64b5f6' }} />
                                    専門家からのアドバイス：内見は必ず朝・夜の状況や、実際の音も体感しましょう！
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
                <Box sx={{ my: 3, textAlign: 'center' }}>

                </Box>
            </Box>

            {/* まとめ */}
            <Box id="summary" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめ：内見で「納得」と「安心」を手に入れよう
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、物件選びだけでなく「人」や「運営の仕組み」も含めた総合的な判断が大切です。そのためには、内見で五感を使って細部まで確認し、自分に合う環境かどうかをしっかり見極めることが後悔しないコツ。<br />
                    チェックリストや質問リストを活用して、安心の新生活スタートを応援します！
                </Typography>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button
                        variant="outlined"
                        size="large"
                        color="primary"
                        startIcon={<EventNote />}
                        href="#checklist"
                        onClick={() => scrollToSection('checklist')}
                    >
                        内見チェックリストをもう一度見る
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-naiken-checklist',
    slug: 'roomshare-naiken-checklist',
    title: 'ルームシェアの「内見」チェックリスト',
    excerpt: '物件内見時のポイント 質問すべき項目 チェックリストのダウンロード 体験談',
    coverImage: '/images/blog/cover-roomshare-naiken-checklist_roomshare-naiken-checklist.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子。木目調の家具や観葉植物が配置され、温かく落ち着いた雰囲気が伝わる。',
    publishedAt: '2024-06-25T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '内見', '住まい選び'],
    tags: ['ルームシェア', '内見チェックリスト', 'シェアハウス', '質問リスト', '体験談'],
    author: {
        name: '高橋 未来',
        avatar: '/images/avatars/mirai-takahashi.jpg',
        bio: 'シェアハウス運営・入居サポートの現場経験多数。住みやすいルームシェアの選び方や、内見チェックのコツを発信しています。',
        profilePicture: '/images/avatars/mirai-takahashi.jpg'
    },
    viewCount: 1000,
    contentComponent: PostContent
};

export default post;